import { area, centroid } from "@turf/turf";
import type { Feature, FeatureCollection, Geometry, Polygon, Position } from "geojson";
import { inject, injectable } from "inversify";
import proj4 from "proj4";
import type { IService, LonLat, XY } from "@designboticteam/core";
import { MapConfigService } from "../MapConfigService";
import type { CRS, CRSCoordinateType } from "./types";

const EARTH_RADIUS_M = 6378137;

// TODO:
// - Get rid of `any`
// - Add unit tests
@injectable()
class ProjectionService implements IService {
	public readonly serviceName = "🗺️ ProjectionService";

	constructor(@inject(MapConfigService) private readonly mapConfigService: MapConfigService) {}

	public initialize(): void {
		// Poland 2000 / CS2000 / Zone V (15°E) - EPSG:2176
		proj4.defs("EPSG:2176", "+proj=tmerc +lat_0=0 +lon_0=15 +k=0.999923 +x_0=5500000 +y_0=0 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs +type=crs");

		// Poland 2000 / CS2000 / Zone VI (18°E) - EPSG:2177
		proj4.defs("EPSG:2177", "+proj=tmerc +lat_0=0 +lon_0=18 +k=0.999923 +x_0=6500000 +y_0=0 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs +type=crs");

		// Poland 2000 / CS2000 / Zone VII (21°E) - EPSG:2178
		proj4.defs("EPSG:2178", "+proj=tmerc +lat_0=0 +lon_0=21 +k=0.999923 +x_0=7500000 +y_0=0 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs +type=crs");

		// Poland 2000 / CS2000 / Zone VIII (24°E) - EPSG:2179
		proj4.defs("EPSG:2179", "+proj=tmerc +lat_0=0 +lon_0=24 +k=0.999923 +x_0=8500000 +y_0=0 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs +type=crs");

		// Poland 1992 / ETRS89 / Poland CS92 (single nationwide zone) - EPSG:2180
		// Note: EPSG:2180 uses y_0=-5300000 (reduced coordinates)
		proj4.defs(
			"EPSG:2180",
			"+proj=tmerc +lat_0=0 +lon_0=19 +k=0.9993 +x_0=500000 +y_0=-5300000 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs +type=crs",
		);

		// Germany / ETRS89 UTM zone 32N
		proj4.defs("EPSG:25832", "+proj=utm +zone=32 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs");

		// Germany / ETRS89 UTM zone 33N
		proj4.defs("EPSG:25833", "+proj=utm +zone=33 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs");

		// Germany / DHDN 3-degree Gauss-Kruger zone 3
		proj4.defs(
			"EPSG:31467",
			"+proj=tmerc +lat_0=0 +lon_0=9 +k=1 +x_0=3500000 +y_0=0 +ellps=bessel +towgs84=598.1,73.7,418.2,0.202,0.045,-2.455,6.7 +units=m +no_defs",
		);

		// Germany / DHDN 3-degree Gauss-Kruger zone 4
		proj4.defs(
			"EPSG:31468",
			"+proj=tmerc +lat_0=0 +lon_0=12 +k=1 +x_0=4500000 +y_0=0 +ellps=bessel +towgs84=598.1,73.7,418.2,0.202,0.045,-2.455,6.7 +units=m +no_defs",
		);

		// Lithuania / LKS94
		proj4.defs("EPSG:3346", "+proj=tmerc +lat_0=0 +lon_0=24 +k=0.9998 +x_0=500000 +y_0=0 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs");

		// Lithuania / LKS92 (Pulkovo 1942 / Krasovsky 1940)
		proj4.defs(
			"EPSG:2600",
			"+proj=tmerc +lat_0=0 +lon_0=24 +k=0.9998 +x_0=500000 +y_0=0 +ellps=krass +towgs84=-33.4,-146.6,-76.3,-0.359,-0.053,0.844,-0.84 +units=m +no_defs",
		);
	}

	public dispose(): void {}

	/** Transforms coordinates between two coordinate reference systems. */
	public transformCoordinates<F extends CRS, T extends CRS>(from: F, to: T, coords: CRSCoordinateType[F]): CRSCoordinateType[T] {
		return proj4(from, to, coords);
	}

	/** Transforms an array of coordinates between two coordinate reference systems. */
	public transformCoordinatesArray<F extends CRS, T extends CRS>(from: F, to: T, coordsArray: Array<CRSCoordinateType[F]>): Array<CRSCoordinateType[T]> {
		return coordsArray.map((coord) => proj4(from, to, coord));
	}

	/** Converts WGS84 (EPSG:4326) to Web Mercator (EPSG:3857). */
	public wgs84ToWebMercator(coords: LonLat): XY {
		return this.transformCoordinates("EPSG:4326", "EPSG:3857", coords);
	}

	/** Converts Web Mercator (EPSG:3857) to WGS84 (EPSG:4326). */
	public webMercatorToWgs84(coords: XY): LonLat {
		return this.transformCoordinates("EPSG:3857", "EPSG:4326", coords);
	}

	/**
	 * Converts WGS84 coordinates to real ground meters relative to the configured origin
	 * ([x, z] = [East, North]). Uses a local equirectangular projection at the origin's
	 * latitude — accurate within ~1 cm at distances up to ~1 km, sufficient for the
	 * project area sizes this app supports.
	 */
	public wgs84ToRelativeMeters(coords: LonLat): XY {
		const [originLng, originLat] = this.mapConfigService.origin;
		const cosLat = Math.cos((originLat * Math.PI) / 180);
		const x = ((coords[0] - originLng) * Math.PI * EARTH_RADIUS_M * cosLat) / 180;
		const z = ((coords[1] - originLat) * Math.PI * EARTH_RADIUS_M) / 180;
		return [x, z];
	}

	/**
	 * @description Converts relative position in web mercator units to WGS84
	 * [x, z] = [East, North]

	 * @param coords - Position relative to the map origin (in web mercator units)
	 */
	public relativeMetersToWgs84(coords: XY): LonLat {
		const [originLng, originLat] = this.mapConfigService.origin;
		const cosLat = Math.cos((originLat * Math.PI) / 180);
		const lng = originLng + (coords[0] * 180) / (Math.PI * EARTH_RADIUS_M * cosLat);
		const lat = originLat + (coords[1] * 180) / (Math.PI * EARTH_RADIUS_M);
		return [lng, lat];
	}

	/**
	 * Converts WGS84 coordinates to real meters in scene world space, relative to the configured origin.
	 * Returns [x, z] = [East, North] in real meters — matches THREE.js world units (1 unit = 1 m)
	 * as set up by the renderer's model matrix (`MercatorCoordinate.meterInMercatorCoordinateUnits()`).
	 *
	 * Web Mercator metres differ from real meters by `1 / cos(latitude)` (≈1.7 at 54°N);
	 * this method applies the cos(originLat) correction. Precision is highest near the origin
	 * and sufficient for objects within a few km (e.g. cadastral parcels in a city).
	 */
	public wgs84ToScene(coords: LonLat): XY {
		const [ox, oy] = this.wgs84ToWebMercator(this.mapConfigService.origin);
		const [px, py] = this.wgs84ToWebMercator(coords);
		const cosLat = Math.cos((this.mapConfigService.origin[1] * Math.PI) / 180);
		return [(px - ox) * cosLat, (py - oy) * cosLat];
	}

	/**
	 * Converts WGS84 to Poland CS92 (EPSG:2180).
	 * https://epsg.io/2180
	 */
	public wgs84ToPolandCs92(coords: LonLat): XY {
		return this.transformCoordinates("EPSG:4326", "EPSG:2180", coords);
	}

	/**
	 * Converts Poland CS92 (EPSG:2180) to WGS84.
	 * https://epsg.io/2180
	 */
	public polandCs92ToWgs84(coords: XY): LonLat {
		return this.transformCoordinates("EPSG:2180", "EPSG:4326", coords);
	}

	public getPoland2000Zone(lon: number): CRS {
		if (lon < 16.5) {
			return "EPSG:2176"; // Zone V (15°E)
		}

		if (lon < 19.5) {
			return "EPSG:2177"; // Zone VI (18°E)
		}

		if (lon < 22.5) {
			return "EPSG:2178"; // Zone VII (21°E)
		}

		return "EPSG:2179"; // Zone VIII (24°E)
	}

	public wgs84ToPoland2000(coords: LonLat): { epsg: CRS; point: XY } {
		const zone = this.getPoland2000Zone(coords[0]);
		const point = this.transformCoordinates("EPSG:4326", zone, coords);
		return { epsg: zone, point };
	}

	public inferPolishCRSFromXY(x: number, _y: number): CRS | null {
		if (x > 5_400_000 && x < 6_000_000) {
			return "EPSG:2176";
		}

		if (x > 6_400_000 && x < 7_000_000) {
			return "EPSG:2177";
		}

		if (x > 7_400_000 && x < 8_000_000) {
			return "EPSG:2178";
		}

		if (x > 8_400_000 && x < 9_000_000) {
			return "EPSG:2179";
		}

		if (x > 400_000 && x < 800_000) {
			return "EPSG:2180";
		}

		return null;
	}

	public calculateDistance(coord1: LonLat, coord2: LonLat): number {
		const pt1 = this.transformCoordinates("EPSG:4326", "EPSG:3857", coord1);
		const pt2 = this.transformCoordinates("EPSG:4326", "EPSG:3857", coord2);
		const dx = pt2[0] - pt1[0];
		const dy = pt2[1] - pt1[1];
		return Math.sqrt(dx * dx + dy * dy);
	}

	public calculatePolygonArea(from: CRS, coordinates: [number, number][]): number {
		const lonlat = coordinates.map((c) => this.transformCoordinates(from, "EPSG:4326", c));

		const polygon: Feature<Polygon> = {
			type: "Feature",
			properties: {},
			geometry: {
				type: "Polygon",
				coordinates: [lonlat],
			},
		};

		return area(polygon);
	}

	public calculatePolygonCentroid(from: CRS, coords: [number, number][]) {
		const lonlat = coords.map((c) => this.transformCoordinates(from, "EPSG:4326", c));

		const polygon: Feature<Polygon> = {
			type: "Feature",
			properties: {},
			geometry: { type: "Polygon", coordinates: [lonlat] },
		};

		const c = centroid(polygon);
		return c.geometry.coordinates;
	}

	public transformPolygonCoordinates(from: CRS, to: CRS, polygonCoords: number[][][]): number[][][] {
		return polygonCoords.map((ring) => ring.map((coord) => proj4(from, to, coord)));
	}

	public transformBbox(from: CRS, to: CRS, bbox: [number, number, number, number]): [number, number, number, number] {
		const [minX, minY] = this.transformCoordinates(from, to, [bbox[0], bbox[1]]);
		const [maxX, maxY] = this.transformCoordinates(from, to, [bbox[2], bbox[3]]);

		return [minX, minY, maxX, maxY];
	}

	// TODO: Fix typing for WKT
	// public wktToGeoJSON(wkt: string, fromProjection: CRS = "EPSG:2180"): any {
	// 	const wellknown = (window as any).wellknown;

	// 	if (!wellknown?.parse) {
	// 		throw new Error("Wellknown library not available for WKT parsing");
	// 	}

	// 	const geometry = wellknown.parse(wkt);
	// 	if (!geometry) {
	// 		throw new Error("Failed to parse WKT");
	// 	}

	// 	return this.transformGeoJSON(fromProjection, "EPSG:4326", geometry);
	// }

	public transformGeoJSON(from: CRS, to: CRS, obj: FeatureCollection): FeatureCollection;
	public transformGeoJSON(from: CRS, to: CRS, obj: Feature): Feature;
	public transformGeoJSON(from: CRS, to: CRS, obj: Geometry): Geometry;
	public transformGeoJSON(from: CRS, to: CRS, obj: FeatureCollection | Feature | Geometry): FeatureCollection | Feature | Geometry {
		const clone: FeatureCollection | Feature | Geometry = JSON.parse(JSON.stringify(obj));

		const transformCoord = (c: Position): Position => proj4(from, to, c as [number, number]);

		const transformGeometry = (g: Geometry | null): Geometry | null => {
			if (g === null) {
				return null;
			}

			switch (g.type) {
				case "Point":
					g.coordinates = transformCoord(g.coordinates);
					break;

				case "MultiPoint":
				case "LineString":
					g.coordinates = g.coordinates.map(transformCoord);
					break;

				case "MultiLineString":
				case "Polygon":
					g.coordinates = g.coordinates.map((ring) => ring.map(transformCoord));
					break;

				case "MultiPolygon":
					g.coordinates = g.coordinates.map((poly) => poly.map((ring) => ring.map(transformCoord)));
					break;

				case "GeometryCollection":
					g.geometries = g.geometries.map((sub) => transformGeometry(sub) as Geometry);
					break;
			}

			return g;
		};

		if (clone.type === "FeatureCollection") {
			clone.features = clone.features.map((f) => this.transformGeoJSON(from, to, f));
			return clone;
		}

		if (clone.type === "Feature") {
			clone.geometry = transformGeometry(clone.geometry) as Geometry;
			return clone;
		}

		return transformGeometry(clone) as Geometry;
	}

	public transformFeatureCollection(from: CRS, to: CRS, featureCollection: FeatureCollection) {
		if (!featureCollection || featureCollection.type !== "FeatureCollection") {
			throw new Error("Input must be a GeoJSON FeatureCollection");
		}

		const transformed: FeatureCollection = JSON.parse(JSON.stringify(featureCollection));
		transformed.features = transformed.features.map((feature) => this.transformGeoJSON(from, to, feature));

		return transformed;
	}
}

export { ProjectionService };
